import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Field, Input } from '@/components/ui';
import type { ConnectFormProps } from '@/components/nodes/ConnectDrawer';
import { useRegisterHost } from '@/hooks/useNodes';
import { errorField, errorText } from '@/utils/errors';
import { isSlug } from '@/utils/validation';

/**
 * An external server (ADR-0033): one where sites already run — AI GF's, a
 * news site's — that the panel backs up and watches without running
 * anything of its own there. Any server with nginx and systemd; it does not
 * have to be in 2d2s.
 */
export function ConnectHostForm({ formId, onIssued, onStatus }: ConnectFormProps): ReactNode {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const register = useRegisterHost({ onSuccess: onIssued });

  const field = register.isError ? errorField(register.error) : null;
  const valid = isSlug(name) && name.length >= 2 && /^\d{1,3}(\.\d{1,3}){3}$/.test(address.trim());

  useEffect(() => onStatus({ ready: valid, busy: register.isPending }), [valid, register.isPending, onStatus]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (valid) register.mutate({ name, address: address.trim() });
  }

  return (
    <form id={formId} className="stack" onSubmit={submit} noValidate>
      <p className="field-hint">
        После подключения агент найдёт сайты на сервере, сопоставит их с сайтами панели по доменам и будет ежедневно создавать
        их резервные копии.
      </p>
      <Field
        label="Название"
        htmlFor="host-name"
        hint="Латиница в нижнем регистре, цифры и дефис"
        {...(field === 'name' ? { error: errorText(register.error) } : {})}
      >
        <Input id="host-name" value={name} onChange={(event) => setName(event.target.value.toLowerCase())} placeholder="aigf-server" autoComplete="off" />
      </Field>
      <Field
        label="IP-адрес"
        htmlFor="host-address"
        hint="Внешний IPv4-адрес сервера"
        {...(field === 'address' ? { error: 'Нужен IPv4-адрес' } : {})}
      >
        <Input id="host-address" value={address} onChange={(event) => setAddress(event.target.value)} placeholder="45.140.14.6" inputMode="decimal" autoComplete="off" />
      </Field>
      {register.isError && !field ? (
        <p className="field-error" role="alert">
          {errorText(register.error)}
        </p>
      ) : null}
    </form>
  );
}
